import { useLazyGetCurrenciesQuery } from '@entities/currency';
import { TOrder, TOrderType, TOrderTableColumns } from '@entities/orders';
import { IOrderDepositTableColumns } from '@entities/orders/deposit';
import { IOrderWithdrawalTableColumns } from '@entities/orders/withdrawal';
import { TUserRoles, useUserRole } from '@entities/user';
import { ModalViewEntity } from '@features/modal-entity-view';
import { QRCodeLink } from '@features/qr';
import ReduceAndCopyString from '@features/reduce-and-copy-string';
import { ITableActionsConfig, TableActionBtns } from '@features/table-action-btns';
import TableCheckAllRows from '@features/table-check-all-rows';
import TableCheckOneRow from '@features/table-check-one-row';
import {
  FilterControlBlock,
  FilterDateRange,
  FilterInput,
  FilterSelect,
  FilterSelectSync,
} from '@features/table-filter';
import { getSelectOptions } from '@shared/components/Form';
import RequestStatusLabel from '@shared/components/RequestStatusLabel';
import { formatAmount } from '@shared/utils/formatAmount';
import { removeKeysFromObject } from '@shared/utils/removeKeysFromObject';

import { ApproveOrder } from '../components/ApproveOrder';
import { ApproveOrderKRW } from '../components/ApproveOrderKRW';
import { BulkOrders } from '../components/BulkOrders';
import { ViewSlip } from '../components/ViewSlip';
import { WebHookStatus } from '../components/WebHookStatus';

interface IProps {
  type: TOrderType;
  refetch?: () => void;
}

export const useTableSettings = ({ type, refetch }: IProps) => {
  const userRole = useUserRole();
  const [getCurrencyFilterOptions] = useLazyGetCurrenciesQuery();
  // THead settings
  const depositColumnTitles: IOrderDepositTableColumns = {
    id: 'Order ID',
    human_hash: 'Short ID',
    request_id: 'Request ID',
    requested_amount: 'Requested amount',
    actual_amount: 'Actual amount',
    currency: 'Currency',
    method: 'Method',
    merchant_short_name: 'Merchant short name',
    customer_transaction_id: 'UTR',
    created_at: 'Create date',
    updated_at: 'Update date',
    merchant_order_id: 'Merchant order ID',
    merchant_customer_id: 'Merchant customer ID',
    status: 'Status',
    status_reason: 'Status reason',
    strategy: 'Strategy',
    webhook_http_status_code: 'Webhook status',
    slip_path: 'Slip',
  };

  const withdrawalColumnTitles: IOrderWithdrawalTableColumns = {
    id: 'Order ID',
    human_hash: 'Short ID',
    request_id: 'Request ID',
    customer_account_name: 'Beneficiary Name',
    customer_bank_account: 'Account Number',
    customer_bank_code: 'IFSC Code',
    requested_amount: 'Requested amount',
    actual_amount: 'Actual amount',
    currency: 'Currency',
    method: 'Method',
    merchant_short_name: 'Merchant short name',
    customer_transaction_id: 'UTR',
    created_at: 'Create date',
    updated_at: 'Update date',
    merchant_order_id: 'Merchant order ID',
    merchant_customer_id: 'Merchant customer ID',
    status: 'Status',
    status_reason: 'Status reason',
    webhook_http_status_code: 'Webhook status',
  };

  type TColumnTitles = {
    [role in TOrderType]: {
      [type in TUserRoles]:
        | Partial<IOrderDepositTableColumns>
        | Partial<IOrderWithdrawalTableColumns>;
    };
  };
  const getColumnTitles = (type: TOrderType, userRole: TUserRoles) => {
    if (!type || !userRole) return {} as TOrderTableColumns;

    const removeDepositKeys: (keyof IOrderDepositTableColumns)[] = [
      'request_id',
      'human_hash',
      'status_reason',
      'strategy',
    ];

    const removeWithdrawalKeys: (keyof IOrderWithdrawalTableColumns)[] = [
      'request_id',
      'human_hash',
      'status_reason',
    ];

    if (userRole === 'merchant') {
      removeDepositKeys.push('merchant_short_name');
      removeWithdrawalKeys.push('merchant_short_name');
    }

    const columnTitles: TColumnTitles = {
      deposit: {
        admin: depositColumnTitles,
        merchant: removeKeysFromObject(depositColumnTitles, removeDepositKeys),
        operator: depositColumnTitles,
        provider: depositColumnTitles,
        user: depositColumnTitles,
      },
      withdrawal: {
        admin: withdrawalColumnTitles,
        merchant: removeKeysFromObject(withdrawalColumnTitles, removeWithdrawalKeys),
        operator: withdrawalColumnTitles,
        provider: withdrawalColumnTitles,
        user: withdrawalColumnTitles,
      },
    };

    return columnTitles[type][userRole] as TOrderTableColumns;
  };

  const filterStatuses =
    type === 'deposit'
      ? ['pending', 'new', 'chargeback', 'success', 'failed', 'expired', 'cancelled', 'refunded']
      : ['pending', 'new', 'success', 'failed', 'refunded'];

  const filterStrategies = [
    { id: 'utr', label: 'UTR' },
    { id: 'short_id', label: 'Short ID' },
    { id: 'upi_id', label: 'UPI ID' },
    { id: 'full_name', label: 'Full Name' },
  ];

  if (type === 'deposit')
    filterStrategies.push(
      { id: 'intent', label: 'Intent' },
      { id: 'partial_utr', label: 'Partial UTR' },
      { id: 'amount', label: 'Amount' },
    );

  const filterWebhookStatuses = [
    { id: '1', label: '🟡 1xx (Informational)' },
    { id: '2', label: '🟢 2xx (Success)' },
    { id: '3', label: '🟡 3xx (Redirect)' },
    { id: '4', label: '🔴 4xx (Client Error)' },
    { id: '5', label: '🔴 5xx (Server Error)' },
  ];

  const renderFilterElement = (key: keyof TOrder | 'slip_path' | 'customer_profile') => {
    switch (key) {
      case 'status':
        return <FilterSelect name={key} options={getSelectOptions(filterStatuses)} />;
      case 'strategy':
        return <FilterSelect name={key} options={filterStrategies} />;

      case 'created_at':
      case 'updated_at':
        return <FilterDateRange name={key} />;

      case 'webhook_http_status_code':
        return <FilterSelect name={key} options={filterWebhookStatuses} />;

      case 'currency':
        return (
          <FilterSelectSync
            getFilterOptions={getCurrencyFilterOptions}
            relationFilterKey='currency'
            name='currency'
            labelKey='code'
            valueKey='code'
            filterTestModeKey='is_test'
          />
        );

      case 'slip_path':
      case 'customer_profile':
        return null;

      default:
        return <FilterInput name={key} />;
    }
  };

  const getTheadBefore = (orders: TOrder[]) => (
    <th className='hidden-on-mob'>
      <TableCheckAllRows items={orders} />
    </th>
  );
  const getTheadAfter = (orders: TOrder[]) => (
    <th>
      <FilterControlBlock storageKey={`order-${type}`}>
        <BulkOrders refetch={refetch} />
      </FilterControlBlock>
    </th>
  );

  // TBody settings
  const renderRow = (key: keyof TOrderTableColumns, order: TOrder) => {
    if (type === 'deposit') {
      if ((key as string) === 'slip_path' && order.currency === 'KRW') {
        return <ViewSlip orderId={order.id} />;
      }
    }

    switch (key) {
      case 'id':
      case 'merchant_order_id':
        return <ReduceAndCopyString text={order[key]} />;

      case 'request_id':
        return (
          <ReduceAndCopyString
            navigateTo={`/requests/${type}?filter[id]=${order.request_id}`}
            text={order[key]}
            allowedNavigateRoles={['admin', 'operator']}
          />
        );
      case 'customer_transaction_id':
        return (
          <ReduceAndCopyString
            navigateTo={`/transactions/${type}?filter[utr]=${order[key]}`}
            text={order[key]}
            needReduce={false}
            allowedNavigateRoles={['admin', 'operator']}
          />
        );

      case 'actual_amount':
      case 'requested_amount':
        return order[key] && `${formatAmount(String(order[key]))}`;

      case 'status':
        return <RequestStatusLabel status={String(order[key])} />;

      case 'webhook_http_status_code':
        return <WebHookStatus status={String(order[key])} />;

      default:
        return order[key];
    }
  };

  const getTbodyBefore = (order: TOrder) => {
    return (
      <td>
        <TableCheckOneRow id={order.id} isDisabled={false} />
      </td>
    );
  };
  const getTbodyAfter = (order: TOrder) => {
    const renamedLabels: Partial<TOrder> = {
      human_hash: 'Short ID',
      customer_transaction_id: 'UTR',
    };

    const excludeViewKeys: (keyof TOrder)[] = ['hash'];
    if (userRole === 'merchant') {
      excludeViewKeys.push('strategy');
    }

    const additionalData =
      type === 'deposit' && order.traffic !== 'vip' && order.method !== 'bank_transfer'
        ? [{ label: 'QR Code', element: <QRCodeLink order_hash={order.hash} /> }]
        : [];

    const actionBtnsConfig: ITableActionsConfig = {
      view: {
        isEnabled: true,
        isVisible: true,
        popupElem: (
          <ModalViewEntity
            excludeKeys={excludeViewKeys}
            entity={order}
            title='View Order'
            renamedLabels={renamedLabels}
            additionalData={additionalData}
          />
        ),
        popupSize: 'md',
      },
    };

    if (type === 'deposit') {
      if (order.currency === 'KRW') {
        actionBtnsConfig.approve = {
          isEnabled: !order.request_id || order.status === 'pending',
          isVisible: true,
          popupElem: (
            <ApproveOrderKRW
              entityType='order'
              entityId={order.id}
              requestedAmount={order.requested_amount}
              actualAmount={order.actual_amount}
              statusReason={order.status_reason}
              createdAt={order.created_at}
              refetch={refetch}
            />
          ),
          popupSize: 'sm',
          allowedRoles: ['admin', 'operator'],
        };
      } else {
        actionBtnsConfig.approve = {
          isEnabled: !order.request_id || order.status === 'pending',
          isVisible: true,
          popupElem: <ApproveOrder order={order} refetch={refetch} />,
          popupSize: 'sm',
          allowedRoles: ['admin', 'operator'],
        };
      }
    }

    return (
      <td className='action-btns-row'>
        <TableActionBtns config={actionBtnsConfig} />
      </td>
    );
  };

  return {
    columnTitles: getColumnTitles(type, userRole),

    renderFilterElement,
    getTheadBefore,
    getTheadAfter,

    renderRow,
    getTbodyBefore,
    getTbodyAfter,
  };
};
